import { useEffect, useState } from 'react'

export type FooterLocale = 'de-DE' | 'en-US'

type FooterCopy = {
    links: {
        cookieSettings: string
        privacyPolicy: string
        imprint: string
    }
    privacyPolicyModal: {
        title: string
        download: string
    }
    imprintModal: {
        title: string
        company: string
        street: string
        city: string
        email: string
        representedByLabel: string
        representedBy: string
        commercialRegisterLabel: string
        commercialRegister: string
        vatIdLabel: string
        vatId: string
        disputeResolutionPrefix: string
        disputeResolutionSuffix: string
        liabilityForContentsTitle: string
        liabilityForContentsBody: string
        liabilityForLinksTitle: string
        liabilityForLinksBody: string
        copyrightTitle: string
        copyrightBody: string
        termsTitle: string
        termsBody: string
        openSourceTitle: string
        openSourcePrefix: string
        openSourceSuffix: string
    }
}

const FOOTER_COPY: Record<FooterLocale, FooterCopy> = {
    'de-DE': {
        links: {
            cookieSettings: 'Datenschutzeinstellungen',
            privacyPolicy: 'Datenschutzerklärung',
            imprint: 'Impressum',
        },
        privacyPolicyModal: {
            title: 'Datenschutzerklärung',
            download: 'Herunterladen',
        },
        imprintModal: {
            title: 'Impressum',
            company: 'einwert GmbH',
            street: 'Am Kartoffelgarten 14',
            city: '81671 München',
            email: 'E-Mail: info@einwert.com',
            representedByLabel: 'Vertreten durch:',
            representedBy: 'Dr. Christina Mauer & Dr. Maximilian Schlachter',
            commercialRegisterLabel: 'Handelsregister und Handelsregisternummer:',
            commercialRegister: 'Amtsgericht München HRB 275243 (§ 5 Abs. 1 Nr. 4 TMG);',
            vatIdLabel: 'Umsatzsteuer-ID:',
            vatId: 'DE354977192 (§ 5 Abs. 1 Nr. 6 TMG);',
            disputeResolutionPrefix:
                'Die Plattform der Europäischen Kommission zur außergerichtlichen Online-Streitbeilegung finden Sie unter ',
            disputeResolutionSuffix:
                '. Die einwert GmbH ist weder bereit noch verpflichtet, an Streitbeilegungsverfahren vor Verbraucherschlichtungsstellen teilzunehmen. (Art. 14 Abs. 1 ODR und § 37 Abs. 1 VBSG);',
            liabilityForContentsTitle: 'Haftung für Inhalte',
            liabilityForContentsBody:
                'Die Inhalte unserer Seiten wurden mit größter Sorgfalt erstellt. Für die Richtigkeit, Vollständigkeit und Aktualität der Inhalte können wir jedoch keine Gewähr übernehmen. Als Diensteanbieter sind wir gemäß § 7 Abs.1 TMG für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 TMG sind wir als Diensteanbieter jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen. Verpflichtungen zur Entfernung oder Sperrung der Nutzung von Informationen nach den allgemeinen Gesetzen bleiben hiervon unberührt. Eine diesbezügliche Haftung ist jedoch erst ab dem Zeitpunkt der Kenntnis einer konkreten Rechtsverletzung möglich. Bei Bekanntwerden von entsprechenden Rechtsverletzungen werden wir diese Inhalte umgehend entfernen.',
            liabilityForLinksTitle: 'Haftung für Links',
            liabilityForLinksBody:
                'Unser Angebot enthält Links zu externen Webseiten Dritter, auf deren Inhalte wir keinen Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich. Die verlinkten Seiten wurden zum Zeitpunkt der Verlinkung auf mögliche Rechtsverstöße überprüft. Rechtswidrige Inhalte waren zum Zeitpunkt der Verlinkung nicht erkennbar. Eine permanente inhaltliche Kontrolle der verlinkten Seiten ist jedoch ohne konkrete Anhaltspunkte einer Rechtsverletzung nicht zumutbar. Bei Bekanntwerden von Rechtsverletzungen werden wir derartige Links umgehend entfernen.',
            copyrightTitle: 'Urheberrecht',
            copyrightBody:
                'Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem deutschen Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechtes bedürfen der schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers. Downloads und Kopien dieser Seite sind nur für den privaten, nicht kommerziellen Gebrauch gestattet. Soweit die Inhalte auf dieser Seite nicht vom Betreiber erstellt wurden, werden die Urheberrechte Dritter beachtet. Insbesondere werden Inhalte Dritter als solche gekennzeichnet. Sollten Sie trotzdem auf eine Urheberrechtsverletzung aufmerksam werden, bitten wir um einen entsprechenden Hinweis. Bei Bekanntwerden von Rechtsverletzungen werden wir derartige Inhalte umgehend entfernen.',
            termsTitle: 'Besondere Nutzungsbedingungen',
            termsBody:
                'Soweit besondere Bedingungen für einzelne Nutzungen dieser Website von den vorgenannten Paragraphen abweichen, wird an entsprechender Stelle ausdrücklich darauf hingewiesen. In diesem Falle gelten im jeweiligen Einzelfall die besonderen Nutzungsbedingungen.',
            openSourceTitle: 'Open-Source-Hinweise',
            openSourcePrefix: 'Hinweise zu Open-Source-Bestandteilen dieser Website finden Sie unter ',
            openSourceSuffix: '.',
        },
    },
    'en-US': {
        links: {
            cookieSettings: 'Privacy settings',
            privacyPolicy: 'Privacy policy',
            imprint: 'Imprint',
        },
        privacyPolicyModal: {
            title: 'Privacy policy',
            download: 'Download',
        },
        imprintModal: {
            title: 'Imprint',
            company: 'einwert GmbH',
            street: 'Am Kartoffelgarten 14',
            city: '81671 Munich',
            email: 'Email: info@einwert.com',
            representedByLabel: 'Represented by:',
            representedBy: 'Dr. Christina Mauer & Dr. Maximilian Schlachter',
            commercialRegisterLabel: 'Commercial register and registration number:',
            commercialRegister: 'Munich Local Court HRB 275243 (Section 5 (1) No. 4 TMG);',
            vatIdLabel: 'VAT ID:',
            vatId: 'DE354977192 (Section 5 (1) No. 6 TMG);',
            disputeResolutionPrefix: 'The European Commission platform for out-of-court online dispute resolution is available at ',
            disputeResolutionSuffix:
                '. einwert GmbH is neither willing nor obliged to participate in dispute resolution proceedings before a consumer arbitration board. (Art. 14 (1) ODR and Section 37 (1) VSBG);',
            liabilityForContentsTitle: 'Liability for content',
            liabilityForContentsBody:
                'The contents of our pages were created with the utmost care. However, we cannot guarantee the accuracy, completeness or timeliness of the contents. As a service provider, we are responsible for our own content on these pages in accordance with general law pursuant to Section 7 (1) TMG. Pursuant to Sections 8 to 10 TMG, however, as a service provider we are not obliged to monitor transmitted or stored third-party information or to investigate circumstances that indicate illegal activity. Obligations to remove or block the use of information under general law remain unaffected. Liability in this respect is only possible from the time of knowledge of a specific infringement. If we become aware of any such infringements, we will remove the content immediately.',
            liabilityForLinksTitle: 'Liability for links',
            liabilityForLinksBody:
                'Our offer contains links to external third-party websites over whose content we have no influence. Therefore, we cannot assume any liability for this external content. The respective provider or operator of the linked pages is always responsible for their content. The linked pages were checked for possible legal violations at the time of linking. Illegal content was not recognizable at the time of linking. Permanent monitoring of the content of linked pages is not reasonable without concrete evidence of an infringement. If we become aware of any legal violations, we will remove such links immediately.',
            copyrightTitle: 'Copyright',
            copyrightBody:
                'The content and works created by the site operators on these pages are subject to German copyright law. Reproduction, editing, distribution and any kind of use outside the limits of copyright law require the written consent of the respective author or creator. Downloads and copies of this page are only permitted for private, non-commercial use. Insofar as the content on this page was not created by the operator, the copyrights of third parties are respected. In particular, third-party content is identified as such. If you nevertheless become aware of a copyright infringement, please notify us accordingly. If we become aware of any legal violations, we will remove such content immediately.',
            termsTitle: 'Special terms of use',
            termsBody:
                'If special conditions for individual uses of this website deviate from the aforementioned paragraphs, this will be expressly indicated at the relevant point. In such a case, the special terms of use shall apply in the respective individual case.',
            openSourceTitle: 'Open source notices',
            openSourcePrefix: 'Information on open source components of this website is available at ',
            openSourceSuffix: '.',
        },
    },
}

export function resolveFooterLocale(locale?: string | null): FooterLocale {
    return locale?.toLowerCase().startsWith('en') ? 'en-US' : 'de-DE'
}

export function getFooterCopy(locale: FooterLocale) {
    return FOOTER_COPY[locale]
}

function getDocumentLocale() {
    if (typeof document === 'undefined') {
        return undefined
    }

    return document.documentElement.lang || undefined
}

function getNavigatorLocale() {
    if (typeof navigator === 'undefined') {
        return undefined
    }

    return navigator.language || navigator.languages?.[0]
}

export function getPreferredFooterLocale() {
    return resolveFooterLocale(getDocumentLocale() || getNavigatorLocale())
}

export function useFooterLocale(localeOverride?: FooterLocale) {
    const [locale, setLocale] = useState<FooterLocale>(() => localeOverride ?? getPreferredFooterLocale())

    useEffect(() => {
        if (localeOverride) {
            return
        }

        if (typeof window === 'undefined' || typeof document === 'undefined') {
            return
        }

        const updateLocale = () => {
            setLocale(getPreferredFooterLocale())
        }

        updateLocale()
        window.addEventListener('languagechange', updateLocale)

        const observer = new MutationObserver(updateLocale)
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['lang'] })

        return () => {
            window.removeEventListener('languagechange', updateLocale)
            observer.disconnect()
        }
    }, [localeOverride])

    return localeOverride ?? locale
}

export function useFooterCopy(localeOverride?: FooterLocale) {
    return getFooterCopy(useFooterLocale(localeOverride))
}
